import React from "react";
import { motion } from "framer-motion";
import { MapPin, Instagram, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { Wordmark } from "@/components/Wordmark";

const rise = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.12 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ContactItem = ({ label, children, testid }) => (
  <div data-testid={testid} className="text-center md:text-left">
    <p className="text-[10px] uppercase tracking-[0.3em] text-stone mb-3">{label}</p>
    {children}
  </div>
);

export default function Maintenance() {
  return (
    <div
      data-testid="maintenance-screen"
      className="min-h-screen bg-sand text-ink flex flex-col relative overflow-hidden"
    >
      <Seo
        title="Under Construction"
        description="Blue Green Studio is reshaping its digital home. Sustainable, climate-responsive residential architecture in Jayanagar, Bengaluru — reach us by email, Instagram, or visit the studio."
        path="/"
      />

      {/* soft earthy washes */}
      <div className="pointer-events-none absolute -top-48 right-[-12%] w-[65vw] h-[65vw] rounded-full bg-forest/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-25%] left-[-12%] w-[55vw] h-[55vw] rounded-full bg-teal/10 blur-3xl" />

      <motion.header
        variants={rise}
        initial="hidden"
        animate="show"
        custom={0}
        className="px-6 md:px-12 pt-8 flex items-center justify-between relative z-10"
      >
        <Wordmark size="md" />
        <p className="text-[10px] uppercase tracking-[0.35em] text-stone">Bengaluru, India</p>
      </motion.header>

      <main className="flex-1 flex flex-col items-center justify-center text-center px-6 relative z-10 py-16 md:py-20">
        <motion.p
          variants={rise}
          initial="hidden"
          animate="show"
          custom={1}
          className="text-[11px] uppercase tracking-[0.35em] font-bold text-teal mb-7"
        >
          Sustainable · Inclusive · Wellness-focused
        </motion.p>

        <motion.h1
          variants={rise}
          initial="hidden"
          animate="show"
          custom={2}
          data-testid="maintenance-heading"
          className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight"
        >
          Under
          <br />
          <span className="italic text-forest">Construction</span>
        </motion.h1>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 h-px w-28 bg-ink/40 origin-center"
        />

        <motion.p
          variants={rise}
          initial="hidden"
          animate="show"
          custom={3}
          className="mt-10 max-w-xl text-base md:text-lg text-stone leading-[1.8]"
        >
          We are carefully reshaping our digital studio — much like our buildings,
          it takes climate, context and culture. While we craft, our doors and our
          inbox stay open.
        </motion.p>
      </main>

      <motion.footer
        variants={rise}
        initial="hidden"
        animate="show"
        custom={4}
        className="relative z-10 border-t border-ink/10 px-6 md:px-12 py-10"
      >
        <div className="max-w-6xl mx-auto grid gap-10 md:grid-cols-3">
          <ContactItem label="Studio" testid="maintenance-studio">
            <p className="text-sm text-ink/85 leading-relaxed">
              No.12, Hema Nivas, Byrasandra Main Rd,<br />
              1st Block East, Jayanagar,<br />
              Bengaluru, Karnataka 560011
            </p>
            <a
              href="https://maps.app.goo.gl/7Uhh9ckfSVTRNu8B8"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="maintenance-map-link"
              className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-teal hover:text-forest transition-colors"
            >
              <MapPin size={13} />
              View on Google Maps
            </a>
          </ContactItem>

          <ContactItem label="Enquiries" testid="maintenance-enquiries">
            <a
              href="mailto:hello.bluegreen.studio@gmail.com"
              data-testid="maintenance-email-link"
              className="inline-flex items-center gap-2 text-sm text-ink/85 hover:text-teal transition-colors break-all"
            >
              <Mail size={14} className="shrink-0" />
              hello.bluegreen.studio@gmail.com
            </a>
            <p className="mt-3 text-xs text-stone leading-relaxed">
              Tell us about your plot, your dream, your brief —<br className="hidden md:block" />
              we reply within two working days.
            </p>
          </ContactItem>

          <ContactItem label="Follow" testid="maintenance-follow">
            <a
              href="https://www.instagram.com/bluegreen_studio.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="maintenance-instagram-link"
              className="inline-flex items-center gap-2 text-sm text-ink/85 hover:text-teal transition-colors"
            >
              <Instagram size={14} />
              @bluegreen_studio.in
            </a>
            <p className="mt-3 text-xs text-stone leading-relaxed">
              Site visits, earth work and process notes —<br className="hidden md:block" />
              the studio journal continues there.
            </p>
          </ContactItem>
        </div>

        <p className="mt-10 text-center text-[10px] uppercase tracking-[0.3em] text-stone/70">
          Blue Green Studio © 2026
        </p>
      </motion.footer>
    </div>
  );
}
